<template>
  <div class="bg-white rounded-lg shadow-md p-6">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
    </div>

    <!-- Error state -->
    <div v-else-if="loadingError" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-6">
      <strong class="font-bold">Error:</strong>
      <span class="block sm:inline">{{ loadingError }}</span>
    </div>

    <div v-else>
      <!-- Template Categories -->
      <div class="mb-6 border-b border-gray-200 pb-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium">Template Categories</h3>
          <button
            @click="showNewCategoryForm = true"
            class="px-3 py-1 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-white hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Add New Category
          </button>
        </div>
        
        <!-- New Category Form -->
        <div v-if="showNewCategoryForm" class="mb-4 p-4 border border-primary/20 rounded-lg bg-primary/5">
          <h4 class="font-medium mb-2">Create New Template Category</h4>
          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
            <input
              type="text"
              v-model="newCategory.name"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter category name"
            />
          </div>
          <div class="mb-3">
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              v-model="newCategory.description"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter category description"
            />
          </div>
          <div class="flex justify-end space-x-2">
            <button
              @click="showNewCategoryForm = false"
              class="px-3 py-1 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancel
            </button>
            <button
              @click="addNewCategory"
              class="px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Add Category
            </button>
          </div>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            v-for="category in templateCategories"
            :key="category.id"
            @click="selectTemplateCategory(category)"
            class="border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md"
            :class="[selectedCategory === category.id ? 'border-primary bg-primary/5' : 'border-gray-200']"
          >
            <div class="flex items-center">
              <div class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                <svg class="h-5 w-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>
              <div>
                <h4 class="font-medium">{{ category.name }}</h4>
                <p class="text-sm text-gray-500">{{ category.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Template Editor -->
      <div class="mb-6">
        <h3 class="text-lg font-medium mb-2">Template Editor</h3>
        <div class="mb-4">
          <label for="template-name" class="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
          <input
            type="text"
            id="template-name"
            v-model="templateName"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            placeholder="Enter template name"
          />
        </div>
        
        <div class="mb-4">
          <label for="template-subject" class="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
          <input
            type="text"
            id="template-subject"
            v-model="emailSubject"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            placeholder="Enter email subject"
          />
        </div>
        
        <div class="mb-4">
          <label for="template-content" class="block text-sm font-medium text-gray-700 mb-1">Email Content</label>
          
          <!-- Rich Text Editor Toolbar -->
          <div class="border border-gray-300 rounded-t-md bg-gray-50 p-2 flex flex-wrap gap-1">
            <button
              @click.prevent="applyFormatting('bold')"
              class="p-1 rounded hover:bg-gray-200 w-7 h-7 flex items-center justify-center"
              title="Bold"
            >
              <span class="font-bold text-lg">B</span>
            </button>
            <button
              @click.prevent="applyFormatting('italic')"
              class="p-1 rounded hover:bg-gray-200 w-7 h-7 flex items-center justify-center"
              title="Italic"
            >
              <span class="italic text-lg">i</span>
            </button>
            <button
              @click.prevent="applyFormatting('underline')"
              class="p-1 rounded hover:bg-gray-200 w-7 h-7 flex items-center justify-center"
              title="Underline"
            >
              <span class="underline text-lg">U</span>
            </button>
            <span class="w-px h-6 bg-gray-300 mx-1"></span>
            <button
              @click.prevent="applyFormatting('image')"
              class="p-1 rounded hover:bg-gray-200"
              title="Insert Image"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </button>
          </div>
          
          <!-- Rich Text Editor Content Area -->
          <div
            id="template-content"
            contenteditable="true"
            @input="updateEmailContent"
            class="w-full min-h-[250px] px-3 py-2 border border-t-0 border-gray-300 rounded-b-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            placeholder="Enter email content. Use {{variable}} for dynamic content."
          ></div>
        </div>
        
        <div class="mb-4">
          <div class="flex justify-between items-center mb-2">
            <label class="block text-sm font-medium text-gray-700">Available Variables</label>
            <button
              @click="showVariableManager = true"
              class="text-sm text-primary hover:text-primary-dark"
            >
              Manage Variables
            </button>
          </div>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="variable in templateVariables"
              :key="variable.name"
              class="px-2 py-1 bg-gray-100 text-gray-800 rounded-md text-sm cursor-pointer hover:bg-gray-200"
              @click="insertVariable(variable.name)"
              :title="variable.description"
            >
              {{variable.name}}
            </span>
          </div>
        </div>
        
        <div class="flex justify-end space-x-3">
          <button
            @click="resetForm"
            class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Reset
          </button>
          <button
            @click="updateTemplateCategory"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Update Template
          </button>
        </div>
      </div>
      
      <div class="mt-8">
        <h3 class="text-lg font-medium mb-2">Template Preview</h3>
        <div class="border border-gray-300 rounded-md p-4 bg-gray-50">
          <div class="mb-2"><strong>Subject:</strong> {{ emailSubject }}</div>
          <div class="prose max-w-none" v-html="formattedEmailContent"></div>
        </div>
      </div>
    </div>
    
    <!-- Variable Manager Modal -->
    <VariableManagerModal
      :is-open="showVariableManager"
      :variables="templateVariables"
      @close="showVariableManager = false"
      @update:variables="updateTemplateVariables"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { logger, LogCategory } from '@/services/logger';
import VariableManagerModal, { TemplateVariable } from './VariableManagerModal.vue';
import { supabase } from '@/services/supabase';

// Define interfaces for our Supabase data


interface EmailTemplate {
  id: string;
  category: string;
  template_name: string;
  subject: string;
  content: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  is_system?: boolean;
}

export default defineComponent({
  name: 'EmailTemplateManagement',
  components: {
    VariableManagerModal
  },
  emits: ['navigate'],
  setup() {
    const templateName = ref('');
    const emailSubject = ref('');
    const emailContent = ref('');
    const selectedCategory = ref('');
    const showNewCategoryForm = ref(false);
    const newCategory = ref({
      name: '',
      description: ''
    });
    const isLoading = ref(true);
    const loadingError = ref<string | null>(null);
    
    // Template categories with templates
    const templateCategories = ref<{
      id: string;
      category: string;
      name: string;
      description: string;
      template: {
        id?: string;
        name: string;
        subject: string;
        content: string;
      };
    }[]>([]);
    
    // Variable manager state
    const showVariableManager = ref(false);
    const templateVariables = ref<TemplateVariable[]>([]);
    
    const formattedEmailContent = computed(() => {
      // Simple formatting for preview - in a real app, you might use a markdown or HTML renderer
      let content = emailContent.value;
      
      // Highlight variables
      templateVariables.value.forEach(variable => {
        const regex = new RegExp(`{{${variable.name}}}`, 'g');
        content = content.replace(regex, `<span class="bg-yellow-100 px-1 rounded" title="${variable.description}">{{${variable.name}}}</span>`);
      });
      
      // Convert newlines to <br>
      content = content.replace(/\n/g, '<br>');
      
      return content;
    });
    
    // Fetch data from Supabase
    async function fetchData() {
      isLoading.value = true;
      loadingError.value = null;
      
      try {
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          loadingError.value = 'You must be logged in to view email templates';
          isLoading.value = false;
          return;
        }
        
        logger.info('Starting to fetch email template data', LogCategory.DATABASE);
        
        await Promise.all([
          fetchTemplateCategories(),
          fetchTemplateVariables()
        ]);
        
        logger.info('Email template data fetched successfully', LogCategory.DATABASE);
        
        isLoading.value = false;
      } catch (error) {
        loadingError.value = error instanceof Error ? error.message : 'An unknown error occurred';
        isLoading.value = false;
        logger.error('Failed to fetch email template data', LogCategory.DATABASE, { error });
      }
    }
    
    // Fetch templates grouped by category
    async function fetchTemplateCategories() {
      // Fetch templates grouped by category
      const { data: templates, error: templatesError } = await supabase
        .from('email_templates')
        .select('*')
        .order('category');
      
      if (templatesError) {
        throw new Error(`Failed to fetch templates: ${templatesError.message}`);
      }
      
      // Group templates by category
      const categoriesMap = new Map();
      
      // Process each template and group by category
      templates.forEach((template: EmailTemplate) => {
        if (!categoriesMap.has(template.category)) {
          categoriesMap.set(template.category, {
            id: template.id, // Using template id as category id
            category: template.category,
            name: template.category, // Using category value as name
            description: `Templates for ${template.category}`,
            template: {
              id: template.id,
              name: template.template_name,
              subject: template.subject,
              content: template.content
            }
          });
        }
      });
      
      // Convert map to array for our component
      templateCategories.value = Array.from(categoriesMap.values());
      // If there are categories, select the first one
      if (templateCategories.value.length > 0) {
        selectTemplateCategory(templateCategories.value[0]);
      }
    
    }
    
    // Fetch template variables from Supabase
    async function fetchTemplateVariables() {
      const { data, error } = await supabase
        .from('email_template_variables')
        .select('*')
        .order('name');
      
      if (error) {
        throw new Error(`Failed to fetch template variables: ${error.message}`);
      }
      
      // Map the data to our component's format
      templateVariables.value = data.map((variable: any) => ({
        name: variable.name,
        category: variable.category,
        description: variable.description || '',
        exampleValue: variable.example_value || '',
        isSystem: variable.is_system || false
      }));
    }
    
    // Add a new template with a new category
    async function addNewCategory() {
      if (!newCategory.value.name) {
        alert('Please enter a category name');
        return;
      }
      
      const categoryValue = newCategory.value.name;
      
      // Check if category already exists
      if (templateCategories.value.some(cat => cat.category === categoryValue)) {
        alert('A category with this name already exists');
        return;
      }
      
      try {
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('You must be logged in to create categories');
        }
        
        // Create a new template with the new category directly
        const templateData = {
          category: categoryValue,
          template_name: newCategory.value.name,
          subject: '',
          content: `<p>Dear {{firstName}} {{lastName}},</p>
<p>Your message here.</p>
<p>Best regards,<br>
{{companyName}} Team</p>`,
          created_by: session.user.id
        };
        
        logger.info('Creating new template with new category', LogCategory.DATABASE, { templateData });
        
        const { data: newTemplateData, error: templateError } = await supabase
          .from('email_templates')
          .insert(templateData)
          .select()
          .single();
        
        if (templateError) {
          throw new Error(`Failed to create template: ${templateError.message}`);
        }
        
        logger.info('Template with new category created successfully', LogCategory.DATABASE, { newTemplateData });
        
        // Add the new template/category to our local state
        const newCategoryWithTemplate = {
          id: newTemplateData.id,
          category: newTemplateData.category,
          name: newTemplateData.category,
          description: newCategory.value.description || `Templates for ${newTemplateData.category}`,
          template: {
            id: newTemplateData.id,
            name: newTemplateData.template_name,
            subject: newTemplateData.subject,
            content: newTemplateData.content
          }
        };
        
        templateCategories.value.push(newCategoryWithTemplate);
        
        // Reset the form
        newCategory.value = { name: '', description: '' };
        showNewCategoryForm.value = false;
        
        // Select the new category
        selectTemplateCategory(newCategoryWithTemplate);
        
        logger.info(`New template category "${newCategory.value.name}" created`, LogCategory.DATABASE);
      } catch (error) {
        alert(error instanceof Error ? error.message : 'Failed to create category');
        logger.error('Failed to create template category', LogCategory.DATABASE, { error });
      }
    }
    
    // Select a template category
    function selectTemplateCategory(category: {id: string, category: string, name: string, description: string, template: any}) {
      selectedCategory.value = category.id;
      
      // Load the template for this category
      const selectedCategoryObj = templateCategories.value.find(cat => cat.id === category.id);
      if (selectedCategoryObj && selectedCategoryObj.template) {
        templateName.value = selectedCategoryObj.template.name;
        emailSubject.value = selectedCategoryObj.template.subject;
        emailContent.value = selectedCategoryObj.template.content;
        
        // Update the content in the rich text editor
        const editorElement = document.getElementById('template-content');
        if (editorElement) {
          editorElement.innerHTML = emailContent.value;
        }
      }
    }
    
    // Update email content from the rich text editor
    function updateEmailContent(event: Event) {
      const target = event.target as HTMLElement;
      emailContent.value = target.innerHTML;
    }
    
    // Apply formatting to the rich text editor
    function applyFormatting(format: string) {
      document.execCommand('styleWithCSS', false, 'true');
      
      let imgUrl;
      
      switch (format) {
        case 'bold':
          document.execCommand('bold', false);
          break;
        case 'italic':
          document.execCommand('italic', false);
          break;
        case 'underline':
          document.execCommand('underline', false);
          break;
        case 'image':
          imgUrl = prompt('Enter image URL:');
          if (imgUrl) {
            document.execCommand('insertImage', false, imgUrl);
          }
          break;
      }
      
      // Update the email content after formatting
      const editorElement = document.getElementById('template-content');
      if (editorElement) {
        emailContent.value = editorElement.innerHTML;
      }
    }
    
    // Insert variable at cursor position in the rich text editor
    function insertVariable(variable: string) {
      // Insert variable at cursor position in the rich text editor
      const editorElement = document.getElementById('template-content');
      if (!editorElement) return;
      
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Check if the selection is within the editor
        if (editorElement.contains(range.commonAncestorContainer)) {
          const variableText = `{{${variable}}}`;
          
          // Create a text node with the variable
          const variableNode = document.createTextNode(variableText);
          
          // Insert the node at the current cursor position
          range.deleteContents();
          range.insertNode(variableNode);
          
          // Move cursor after the inserted variable
          range.setStartAfter(variableNode);
          range.setEndAfter(variableNode);
          selection.removeAllRanges();
          selection.addRange(range);
          
          // Update the email content
          emailContent.value = editorElement.innerHTML;
        } else {
          // If selection is outside editor, append to the end
          editorElement.innerHTML += `{{${variable}}}`;
          emailContent.value = editorElement.innerHTML;
        }
      } else {
        // Fallback if no selection: append to the end
        editorElement.innerHTML += `{{${variable}}}`;
        emailContent.value = editorElement.innerHTML;
      }
    }
    
    // Update the template for the current category
    async function updateTemplateCategory() {
      if (!selectedCategory.value || !templateName.value || !emailSubject.value || !emailContent.value) {
        alert('Please select a category and fill in all fields');
        return;
      }
      
      try {
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('You must be logged in to update templates');
        }
        
        logger.info('Starting template update', LogCategory.DATABASE, {
          selectedCategory: selectedCategory.value,
          templateName: templateName.value
        });
        
        // Find the selected category
        const categoryIndex = templateCategories.value.findIndex(cat => cat.id === selectedCategory.value);
        if (categoryIndex < 0) {
          throw new Error('Selected category not found');
        }
        
        const category = templateCategories.value[categoryIndex];
        logger.info('Found category', LogCategory.DATABASE, { category });
        
        // Check if this category already has a template
        if (category.template.id) {
          // Prepare template data for update
          const updateData = {
            template_name: templateName.value,
            subject: emailSubject.value,
            content: emailContent.value,
            updated_at: new Date().toISOString()
          };
          
          logger.info('Attempting to update existing template', LogCategory.DATABASE, {
            templateId: category.template.id,
            updateData
          });
          
          // Try to update the existing template
          const { data: updateResult, error: updateError } = await supabase
            .from('email_templates')
            .update(updateData)
            .eq('id', category.template.id)
            .select();
          
          if (updateError) {
            logger.error('Failed to update template, will try to create a new one', LogCategory.DATABASE, { updateError });
            
            // If update fails, create a new template
            const insertData = {
              template_name: templateName.value,
              subject: emailSubject.value,
              content: emailContent.value,
              category: category.category,
              created_by: session.user.id,
              updated_at: new Date().toISOString(),
              is_system: false
            };
            
            logger.info('Creating new template as fallback', LogCategory.DATABASE, { insertData });
            
            const { data: insertResult, error: insertError } = await supabase
              .from('email_templates')
              .insert(insertData)
              .select()
              .single();
            
            if (insertError) {
              throw new Error(`Failed to create template: ${insertError.message}`);
            }
            
            // Update the template ID in our local state
            templateCategories.value[categoryIndex].template.id = insertResult.id;
            logger.info('New template created successfully as fallback', LogCategory.DATABASE, { insertResult });
          } else {
            logger.info('Template updated successfully', LogCategory.DATABASE, { updateResult });
          }
        } else {
          // Create new template for this category
          const insertData = {
            template_name: templateName.value,
            subject: emailSubject.value,
            content: emailContent.value,
            category: category.category,
            created_by: session.user.id,
            updated_at: new Date().toISOString(),
            is_system: false
          };
          
          logger.info('Creating new template (no existing template)', LogCategory.DATABASE, { insertData });
          
          const { data: insertResult, error: insertError } = await supabase
            .from('email_templates')
            .insert(insertData)
            .select()
            .single();
          
          if (insertError) {
            throw new Error(`Failed to create template: ${insertError.message}`);
          }
          
          // Update the template ID in our local state
          templateCategories.value[categoryIndex].template.id = insertResult.id;
          logger.info('New template created successfully', LogCategory.DATABASE, { insertResult });
        }
        
        // Update our local state
        templateCategories.value[categoryIndex].template = {
          id: templateCategories.value[categoryIndex].template.id,
          name: templateName.value,
          subject: emailSubject.value,
          content: emailContent.value
        };
        
        // Show success message
        const successMessage = document.createElement('div');
        successMessage.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
        successMessage.textContent = `Template "${templateName.value}" successfully saved!`;
        document.body.appendChild(successMessage);
        
        // Remove the success message after 3 seconds
        setTimeout(() => {
          document.body.removeChild(successMessage);
        }, 3000);
        
        logger.info(`Template for category "${templateCategories.value[categoryIndex].name}" updated`, LogCategory.DATABASE);
      } catch (error) {
        alert(error instanceof Error ? error.message : 'Failed to update template');
        logger.error('Failed to update template', LogCategory.DATABASE, { error });
      }
    }
    
    // Update template variables from the modal
    async function updateTemplateVariables(newVariables: TemplateVariable[]) {
      try {
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('You must be logged in to update template variables');
        }
        
        logger.info('Starting template variables update', LogCategory.DATABASE);
        
        // First, get the current variables from Supabase
        const { data: currentVariables, error: fetchError } = await supabase
          .from('email_template_variables')
          .select('*');
        
        if (fetchError) {
          throw new Error(`Failed to fetch current variables: ${fetchError.message}`);
        }
        
        // Identify variables to add, update, or delete
        const variablesToAdd: TemplateVariable[] = [];
        const variablesToUpdate: (TemplateVariable & { id: string })[] = [];
        const variableIdsToDelete: string[] = [];
        
        // Find variables to add or update
        for (const newVar of newVariables) {
          const existingVar = currentVariables.find((v: any) => v.name === newVar.name);
          
          if (!existingVar) {
            // This is a new variable
            variablesToAdd.push(newVar);
          } else if (
            existingVar.category !== newVar.category ||
            existingVar.description !== newVar.description ||
            existingVar.example_value !== newVar.exampleValue
          ) {
            // This variable needs to be updated
            variablesToUpdate.push({
              ...newVar,
              id: existingVar.id
            });
          }
        }
        
        // Find variables to delete
        for (const currentVar of currentVariables) {
          if (!currentVar.is_system && !newVariables.some(v => v.name === currentVar.name)) {
            variableIdsToDelete.push(currentVar.id);
          }
        }
        
        logger.info('Variables to process', LogCategory.DATABASE, {
          toAdd: variablesToAdd.length,
          toUpdate: variablesToUpdate.length,
          toDelete: variableIdsToDelete.length
        });
        
        // Perform the database operations
        const operations: any[] = [];
        
        // Add new variables
        if (variablesToAdd.length > 0) {
          operations.push(
            supabase
              .from('email_template_variables')
              .insert(
                variablesToAdd.map(v => ({
                  name: v.name,
                  category: v.category,
                  description: v.description,
                  example_value: v.exampleValue,
                  is_system: v.isSystem || false,
                  created_by: session.user.id
                }))
              )
          );
        }
        
        // Update existing variables
        for (const variable of variablesToUpdate) {
          operations.push(
            supabase
              .from('email_template_variables')
              .update({
                category: variable.category,
                description: variable.description,
                example_value: variable.exampleValue
              })
              .eq('id', variable.id)
          );
        }
        
        // Delete variables
        if (variableIdsToDelete.length > 0) {
          operations.push(
            supabase
              .from('email_template_variables')
              .delete()
              .in('id', variableIdsToDelete)
          );
        }
        
        // Execute all operations
        await Promise.all(operations);
        
        // Update our local state
        templateVariables.value = newVariables;
        
        logger.info('Template variables updated', LogCategory.DATABASE);
      } catch (error) {
        alert(error instanceof Error ? error.message : 'Failed to update variables');
        logger.error('Failed to update template variables', LogCategory.DATABASE, { error });
        
        // Refresh the variables from the database
        await fetchTemplateVariables();
      }
    }
    
    function resetForm() {
      // If a category is selected, reset to its default template
      if (selectedCategory.value) {
        const category = templateCategories.value.find(cat => cat.id === selectedCategory.value);
        if (category) {
          selectTemplateCategory(category);
          return;
        }
      }
      
      // Otherwise clear everything
      templateName.value = '';
      emailSubject.value = '';
      emailContent.value = '';
      
      // Clear the rich text editor
      const editorElement = document.getElementById('template-content');
      if (editorElement) {
        editorElement.innerHTML = '';
      }
    }
    
    // Call fetchData when the component is mounted
    onMounted(() => {
      fetchData();
    });
    
    return {
      templateName,
      emailSubject,
      emailContent,
      selectedCategory,
      templateCategories,
      templateVariables,
      formattedEmailContent,
      showNewCategoryForm,
      showVariableManager,
      newCategory,
      isLoading,
      loadingError,
      selectTemplateCategory,
      updateEmailContent,
      applyFormatting,
      insertVariable,
      updateTemplateCategory,
      resetForm,
      addNewCategory,
      updateTemplateVariables
    };
  }
});
</script>

<style scoped>
.prose {
  line-height: 1.6;
}
</style>
